// Areas panel rendered inline under FloorDetailPage. Mirrors
// FloorListPanel's shape (sub-section heading + Create CTA + table).
// No detail route because the backend exposes POST + GET-list only.

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { areas } from "../../api/endpoints/areas";
import { adminQueryOptions } from "../../api/adminQueryOptions";
import { qk } from "../../api/queryKeys";
import { EmptyState } from "../../components/ui/EmptyState";
import { ErrorBanner } from "../../components/ui/ErrorBanner";
import { Modal } from "../../components/ui/Modal";
import { Pill } from "../../components/ui/Pill";
import { Table } from "../../components/ui/Table";
import type { AreaOut } from "../../api/types/area";

import { AreaCreateForm } from "./AreaCreateForm";

interface AreaListPanelProps {
  accountSlug: string;
  tenantSlug: string;
  siteSlug: string;
  floorSlug: string;
}

export function AreaListPanel({
  accountSlug,
  tenantSlug,
  siteSlug,
  floorSlug,
}: AreaListPanelProps) {
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  const q = useQuery({
    ...adminQueryOptions,
    queryKey: qk.areas(accountSlug, tenantSlug, siteSlug, floorSlug),
    queryFn: () =>
      areas
        .list(accountSlug, tenantSlug, siteSlug, floorSlug)
        .then((r) => r.data),
  });

  return (
    <>
      <header style={panelHeader}>
        <h3 style={panelHeading}>Areas</h3>
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          style={primaryBtn}
        >
          Create area
        </button>
      </header>

      {q.isPending && <p style={{ color: "var(--text-dim)" }}>Loading…</p>}
      {q.error && <ErrorBanner error={q.error} title="Failed to list areas" />}
      {q.data && (
        <Table<AreaOut>
          columns={[
            { key: "slug", header: "Slug" },
            { key: "label", header: "Label" },
            {
              key: "space_type",
              header: "Space type",
              render: (a) => <Pill tone="info">{a.space_type}</Pill>,
            },
          ]}
          rows={q.data}
          rowKey={(a) => a.id}
          empty={
            <EmptyState
              title="No areas on this floor"
              description="Add a kitchen, bedroom, etc. The space_type dropdown reads from the area_space_type catalog."
            />
          }
        />
      )}

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        closeDisabled={creating}
        title={`Create area on ${floorSlug}`}
        width={480}
      >
        <AreaCreateForm
          accountSlug={accountSlug}
          tenantSlug={tenantSlug}
          siteSlug={siteSlug}
          floorSlug={floorSlug}
          onDone={() => setCreateOpen(false)}
          onPendingChange={setCreating}
        />
      </Modal>
    </>
  );
}

const panelHeader: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: 12,
};

const panelHeading: React.CSSProperties = {
  margin: 0,
  fontSize: "0.95rem",
  color: "var(--text-dim)",
  letterSpacing: "0.04em",
  textTransform: "uppercase",
};

const primaryBtn: React.CSSProperties = {
  background: "var(--accent)",
  color: "var(--bg)",
  border: "none",
  padding: "6px 12px",
  borderRadius: 4,
  fontSize: "0.85rem",
  fontWeight: 600,
  cursor: "pointer",
};
